import { Info, Loader2, RotateCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  summarizeEmailDispatch,
  type ContentPublishingDispatch,
  type SuppressionDetail,
} from "@/lib/composerCore";
import type { RecipientRow } from "./dispatchDrawerData";

type Props = {
  dispatch: ContentPublishingDispatch;
  emailSummary: NonNullable<ReturnType<typeof summarizeEmailDispatch>>;
  recipientRows: RecipientRow[];
  suppressionByEmail: Record<string, SuppressionDetail>;
  suppressionLoading: boolean;
  onResendToFailed?: (dispatch: ContentPublishingDispatch, recipients: string[]) => void;
  resending?: boolean;
  canResend?: boolean;
};

export const EmailDispatchSection = ({
  dispatch,
  emailSummary,
  recipientRows,
  suppressionByEmail,
  suppressionLoading,
  onResendToFailed,
  resending,
  canResend,
}: Props) => {
  const failedRecipients = recipientRows.filter((r) => r.status === "rejected").map((r) => r.email);
  const showResend =
    !!onResendToFailed &&
    canResend !== false &&
    failedRecipients.length > 0 &&
    (dispatch.status === "failed" || emailSummary.rejectedCount > 0);

  return (
    <>
      <section className="space-y-1">
        <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Subject</p>
        <p className="rounded-md border border-border bg-background p-2 text-sm">
          {emailSummary.subject ?? "—"}
        </p>
      </section>

      <section className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Accepted</p>
          <p className="text-lg font-semibold">{emailSummary.acceptedCount}</p>
        </div>
        <div className="rounded-md border border-border bg-card p-3">
          <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Rejected</p>
          <p className="text-lg font-semibold">{emailSummary.rejectedCount}</p>
        </div>
      </section>

      {showResend ? (
        <section className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
          <p className="text-xs text-foreground">
            {failedRecipients.length} recipient{failedRecipients.length === 1 ? "" : "s"} failed. Resend only to those addresses.
          </p>
          <Button
            size="sm"
            onClick={() => onResendToFailed?.(dispatch, failedRecipients)}
            disabled={resending === true}
          >
            {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
            Resend to failed recipients only
          </Button>
        </section>
      ) : null}

      <section className="space-y-1">
        <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
          Recipients ({recipientRows.length})
        </p>
        {recipientRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No recipients recorded.</p>
        ) : (
          <ul className="space-y-1 rounded-md border border-border bg-background p-2 text-xs">
            {recipientRows.map((row, idx) => {
              const suppression =
                row.status === "bounced" || row.status === "complained"
                  ? suppressionByEmail[row.email.toLowerCase()] ?? null
                  : null;
              return (
                <li
                  key={`${row.email}-${idx}`}
                  className="flex flex-wrap items-start justify-between gap-2 border-b border-border/60 py-1 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 font-mono">
                      <span>{row.email}</span>
                      {row.status === "bounced" || row.status === "complained" ? (
                        <HoverCard openDelay={150}>
                          <HoverCardTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-muted-foreground hover:text-foreground"
                              aria-label="Suppression details"
                            >
                              <Info className="h-3 w-3" />
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent side="top" align="end" className="w-72 text-xs">
                            {suppressionLoading && !suppression ? (
                              <p className="text-muted-foreground">Loading suppression details…</p>
                            ) : suppression ? (
                              <div className="space-y-1.5">
                                <p>
                                  <span className="font-medium text-foreground">Reason:</span>{" "}
                                  <span className="capitalize">{suppression.reason}</span>
                                </p>
                                {suppression.metadata?.diagnostic ? (
                                  <p className="break-words">
                                    <span className="font-medium text-foreground">Diagnostic:</span>{" "}
                                    <span className="text-muted-foreground">
                                      {String(suppression.metadata.diagnostic)}
                                    </span>
                                  </p>
                                ) : null}
                                {suppression.metadata?.severity ? (
                                  <p>
                                    <span className="font-medium text-foreground">Severity:</span>{" "}
                                    <span className="text-muted-foreground capitalize">
                                      {String(suppression.metadata.severity)}
                                    </span>
                                  </p>
                                ) : null}
                                {suppression.created_at ? (
                                  <p className="text-muted-foreground">
                                    Recorded {new Date(suppression.created_at).toLocaleString()}
                                  </p>
                                ) : null}
                              </div>
                            ) : (
                              <p className="text-muted-foreground">
                                No suppression record found for this address.
                              </p>
                            )}
                          </HoverCardContent>
                        </HoverCard>
                      ) : null}
                    </div>
                    {row.detail ? (
                      <div
                        className={`mt-0.5 break-all text-[10px] ${
                          row.status === "rejected" ||
                          row.status === "bounced" ||
                          row.status === "complained"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }`}
                      >
                        {row.detail}
                      </div>
                    ) : null}
                  </div>
                  <Badge
                    variant={
                      row.status === "rejected" ||
                      row.status === "bounced" ||
                      row.status === "complained"
                        ? "destructive"
                        : "secondary"
                    }
                    className="shrink-0 capitalize"
                  >
                    {row.status}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
};
